import * as yup from 'yup';
import { dayjsNow, parseDayJs } from './dayjs.type';

/**
 * Custom Yup extension methods for the Application. The main reasoning for these extensions is that the dayjs() instance
 * we were passing into methods such as min() and max() becomes static, so if the users leave the browser open for extended
 * times, this is never recalculated unless hard refreshed.
 * ---------------------------------------------------
 */
yup.addMethod(yup.date, 'annualMinRange', function annualMinRange() {
	return this.test({
		name: 'annualMinRange',
		message: 'date.annualMinRange',
		test(value, context) {
			/** Required() is a separate check. If there is no data, it passes this validation. */
			if (!value) return true;

			const dayJsVal = parseDayJs(String(value));

			/** Required() is a separate check. If there is no data, it passes this validation. */
			if (!dayJsVal) return true;

			if (dayJsVal < dayjsNow().add(-1, 'year')) {
				return context.createError({
					path: '',
					message: 'Date must be within a year of today.'
				});
			}

			return true;
		}
	});
});

yup.addMethod(yup.date, 'annualMaxRange', function annualMaxRange() {
	return this.test({
		name: 'annualMaxRange',
		message: 'date.annualMaxRange',
		test(value, context) {
			/** Required() is a separate check. If there is no data, it passes this validation. */
			if (!value) return true;

			const dayJsVal = parseDayJs(String(value));

			/** Required() is a separate check. If there is no data, it passes this validation. */
			if (!dayJsVal) return true;

			if (dayJsVal > dayjsNow().add(1, 'year')) {
				return context.createError({
					path: '',
					message: 'Date must be within a year of today.'
				});
			}

			return true;
		}
	});
});

yup.addMethod(yup.date, 'containerMaxRange', function containerMinRange() {
	return this.test({
		name: 'containerMaxRange',
		message: 'date.containerMaxRange',
		test(value, context) {
			/** Required() is a separate check. If there is no data, it passes this validation. */
			if (!value) return true;

			const dayJsVal = parseDayJs(String(value));

			/** Required() is a separate check. If there is no data, it passes this validation. */
			if (!dayJsVal) return true;

			if (dayJsVal > dayjsNow().add(4, 'day')) {
				return context.createError({
					path: '',
					message: 'Container must be made available within 4 days'
				});
			}

			/** Valid data was determined */
			return true;
		}
	});
});

yup.addMethod<yup.StringSchema>(yup.string, 'isValidDateRangeString', function isValidDateRangeString() {
	return this.test({
		name: 'isValidDateRangeString',
		message: 'string.isValidDateRangeString',
		test(value, context) {
			if (!value) return true;

			const range = value.split('-');
			const startRangeDateTime = range[0];
			const startRange = startRangeDateTime.split(' ');

			const endRangeTime = range[1]?.trim();

			/** We at least need MM/dd/YY before we can continue */
			if (startRangeDateTime.length < 8) {
				return context.createError({
					message: 'Invalid Date'
				});
			}

			const startRangeDayjs = parseDayJs(startRangeDateTime);
			if (!startRangeDayjs?.isValid()) {
				return context.createError({
					message: 'Invalid Date'
				});
			}

			if (endRangeTime) {
				/** Ensure we have a full valid time first */
				if (endRangeTime.length < 5) {
					return context.createError({
						message: 'Invalid Date'
					});
				}

				const startRangeDate = startRange[0];
				const endRangeDayjs = parseDayJs(`${startRangeDate} ${endRangeTime}`);
				if (!endRangeDayjs?.isValid()) {
					return context.createError({
						message: 'Invalid Date'
					});
				}
			}

			return true;
		}
	});
});

/**
 * Updating the yup module with our extensions above so that TypeScript knows what we are doing.
 */
declare module 'yup' {
	interface StringSchema<
		TType extends yup.Maybe<string> = string | undefined,
		TContext = yup.AnyObject,
		TDefault = undefined,
		TFlags extends yup.Flags = ''
	> extends yup.Schema<TType, TContext, TDefault, TFlags> {
		/**
		 *	Ensure that the input string is a valid Date, DateTime, or Date with Time Range
		 */
		isValidDateRangeString(): this;
	}

	interface DateSchema<
		TType extends yup.Maybe<Date> = Date | undefined,
		TContext = yup.AnyObject,
		TDefault = undefined,
		TFlags extends yup.Flags = ''
	> extends yup.Schema<TType, TContext, TDefault, TFlags> {
		/**
		 * Date validation ensuring the input is within the last year. This extension is necessary due to the fact that using
		 * the base .min({minDate}) method provided by yup will not recalculate the input, which we are using dayjs().
		 */
		annualMinRange(): this;
		/**
		 * Date validation ensuring the input is within the next year. This extension is necessary due to the fact that using
		 * the base .max({maxDate}) method provided by yup will not recalculate the input, which we are using dayjs().
		 */
		annualMaxRange(): this;
		/**
		 *
		 */
		containerMaxRange(): this;
	}
}

export default yup;
